import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, Users, Lightbulb, Laptop, Rocket, 
  ArrowRight, CheckCircle2, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const philanthropySchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Our Philanthropy - Fearless IT Gives Back",
  "description": "Fearless IT is proud to support the GodWork Foundation, empowering youth in Las Vegas with mentorship, technology skills, and entrepreneurial training.",
  "author": {
    "@type": "Organization",
    "name": "Fearless IT"
  },
  "publisher": {
    "@type": "Organization",
    "name": "Fearless IT",
    "url": "https://fearlessit.io"
  }
};

const PhilanthropyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="philanthropy-page">
      <SEO 
        title="Our Philanthropy - Supporting Youth Through Technology"
        description="Fearless IT partners with the GodWork Foundation to empower Las Vegas youth with mentorship, technology skills, and entrepreneurial training. Learn how we give back to our community."
        keywords="Fearless IT philanthropy, GodWork Foundation, Las Vegas youth programs, tech mentorship, community giving, youth empowerment Las Vegas"
        url="/philanthropy"
        image="https://static.prod-images.emergentagent.com/jobs/1c71d773-84d6-4251-9bae-60816a9265db/images/0c614e343a21261068bf0ec22f7aef1df5b7fb93fa3a331026324c1bab3ead04.png"
        structuredData={philanthropySchema}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-rose-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-100 rounded-full mb-6">
            <Heart className="h-4 w-4 text-rose-600" />
            <span className="text-sm font-medium text-rose-700">Giving Back</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="philanthropy-title">
            Our Philanthropy
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #E11D48, #F43F5E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Technology With Purpose. Community With Heart.
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            At Fearless IT, we believe technology should empower everyone. That's why we're proud to support organizations that help young people build brighter futures.
          </p>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 rounded-full mb-4">
                <span className="text-xs font-semibold text-amber-700 uppercase tracking-wider">Our Partner</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-6">
                GodWork Foundation
              </h2>
              <p className="text-lg text-slate-gray mb-6 leading-relaxed">
                The GodWork Foundation is a 501(c)(3) nonprofit organization dedicated to equipping teens and young adults with the mentorship, technology skills, and entrepreneurial training they need to rise above adversity and build lives of purpose and possibility.
              </p>
              <p className="text-lg text-slate-gray mb-6 leading-relaxed">
                Based in Las Vegas and serving youth in multiple cities, GodWork addresses the real challenges young people face: Fear, Anxiety, Doubt, and Depression (the FADDs of Life). They provide practical tools, structured programs, and authentic support.
              </p>
              <a 
                href="https://www.godworkfoundation.org/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-rose-600 font-semibold hover:text-rose-700 transition-colors"
              >
                Visit GodWork Foundation
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-rose-50 rounded-2xl p-8 border border-amber-100">
              <h3 className="text-xl font-bold text-carbon-black mb-6">GodWork Programs</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-carbon-black">The FADDs of Life Mentoring</h4>
                    <p className="text-sm text-slate-gray">Mental wellness and personal development for teens battling anxiety and self-doubt</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                    <Laptop className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-carbon-black">Spark AI Technology</h4>
                    <p className="text-sm text-slate-gray">Technology training including AI, coding, and digital skills for the modern workforce</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-carbon-black">Next Frame Entrepreneurship</h4>
                    <p className="text-sm text-slate-gray">Business skills and entrepreneurial mindset training for aspiring young founders</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Help Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-slate-50 to-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              How Fearless IT Helps
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              We contribute to GodWork Foundation's mission in meaningful ways that align with our expertise and values.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 flex items-center justify-center mb-4">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-carbon-black mb-2">Financial Donations</h3>
              <p className="text-slate-gray text-sm leading-relaxed">
                A portion of our revenue goes directly to supporting GodWork's programs, exposure trips, and youth stipends.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center mb-4">
                <Laptop className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-carbon-black mb-2">Technology Support</h3>
              <p className="text-slate-gray text-sm leading-relaxed">
                We provide pro-bono technology consulting and digital infrastructure support to help GodWork operate efficiently.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-carbon-black mb-2">Mentorship</h3>
              <p className="text-slate-gray text-sm leading-relaxed">
                Our team members volunteer their time and expertise to mentor young people interested in technology careers.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mb-4">
                <Rocket className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-carbon-black mb-2">Career Pathways</h3>
              <p className="text-slate-gray text-sm leading-relaxed">
                We offer internship and job shadowing opportunities for GodWork program graduates exploring tech careers.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center mb-4">
                <Lightbulb className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-carbon-black mb-2">Skills Workshops</h3>
              <p className="text-slate-gray text-sm leading-relaxed">
                We host hands-on technology workshops teaching web development, AI basics, and digital skills to program participants.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-bold text-carbon-black mb-2">Awareness & Advocacy</h3>
              <p className="text-slate-gray text-sm leading-relaxed">
                We actively promote GodWork's mission to our network, helping connect them with donors, sponsors, and volunteers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-r from-rose-50 to-amber-50 rounded-2xl p-8 sm:p-12 border border-rose-100">
            <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-6 text-center">
              Why This Matters to Us
            </h2>
            <div className="space-y-4 text-slate-gray leading-relaxed">
              <p>
                Las Vegas is our home. We've built our business here, and we believe in investing in the future of our community. Too many young people in our city face challenges that make it hard to see a path forward. But with the right support, they can thrive.
              </p>
              <p>
                The GodWork Foundation's approach resonates with us because it's practical, not just inspirational. They give young people real tools, real skills, and real mentorship. That's exactly how we approach technology: solutions that actually work.
              </p>
              <p className="font-semibold text-carbon-black">
                When we help a young person learn to code, build confidence, or see themselves as a future entrepreneur, we're not just doing charity. We're investing in the next generation of problem-solvers, creators, and leaders who will shape Las Vegas for decades to come.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Heart className="h-12 w-12 text-rose-500 mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Join Us in Making a Difference
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Whether you're a business looking to give back, or an individual who wants to support youth in Las Vegas, the GodWork Foundation welcomes your involvement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://donorbox.org/godworkfoundation" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button className="bg-gradient-to-r from-rose-500 to-pink-500 text-white px-8 py-6 text-lg font-semibold rounded-xl hover:from-rose-600 hover:to-pink-600 transition-all w-full sm:w-auto">
                Donate to GodWork Foundation
                <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <a 
              href="https://www.godworkfoundation.org/admissions" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-rose-400 text-rose-600 hover:bg-rose-50 transition-all w-full sm:w-auto">
                Apply for a Program
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default PhilanthropyPage;
